import { AxiosError, AxiosResponse } from "axios";
import api from "@services/axios/apiManager";
import type { SupplierData, LastOrder, LastOrderGasItem } from "@screen/Home/pages/GasShop/products";
import type { Address } from "@utils/store/user/useUser";

/**
 * Kiosk 流程的 API 層。
 * 重用既有的 platform-order-drafts 後端（與 GasShop 同一套草稿系統），
 * 單筆品項 / 草稿增刪改與送單直接 re-export @components/product/API/handleProductAPI，
 * 這裡只補 bootstrap / 建草稿 / 一鍵重訂 / preview 這幾個 kiosk 會用到的高階呼叫。
 */
export {
  addProductItem,
  updateProductItem,
  delProductItem,
  delDraftOrder,
  updateDraftOrder,
  submitOrder,
} from "@components/product/API/handleProductAPI";

interface AuthCtx {
  jwtToken: string;
  xContext: string;
}

/**
 * 從 API 回應中取出 draft 物件。
 * BE 形狀不一致：建立草稿回 `data.draft`（巢狀），單筆品項 add/update 回 `data` 本身（含 draft_id）。
 * 兩種都要支援，否則第一筆之後的加購不會 syncDraft → 購物車不更新。
 * res = handleProductAPI 的回傳 { state, data: axiosRes.data }；axiosRes.data = { status, message, data }。
 */
export function draftFromItemRes(res: any): any | null {
  const inner = res?.data?.data;
  if (!inner) return null;
  return inner.draft || (inner.draft_id ? inner : null);
}

const authHeaders = ({ jwtToken, xContext }: AuthCtx) => ({
  Authorization: `Bearer ${jwtToken}`,
  "X-Context": xContext,
});

export interface BootstrapResult {
  supplier: SupplierData | null;
  addresses: Address[];
}

// bootstrap 去重：同一組 (jwt|context|supplier) 在極短時間內被重複呼叫（開發模式 StrictMode
// 雙呼叫、或上游 re-render/remount 連續觸發）時共用同一個 in-flight promise + 短 TTL 結果快取，
// 避免對後端連打數次同樣的 bootstrap。key 不同（換身分/換 supplier）自然會重新拉。
const BOOTSTRAP_DEDUPE_MS = 1500;
let bootstrapInflight: { key: string; ts: number; promise: Promise<BootstrapResult> } | null = null;

/** 拉 bootstrap，回傳指定 supplier 的資料 + 可用地址清單。 */
export const fetchKioskBootstrap = ({
  jwtToken,
  xContext,
  supplierId,
}: AuthCtx & { supplierId?: string }): Promise<BootstrapResult> => {
  const key = `${jwtToken}|${xContext}|${supplierId ?? ""}`;
  if (bootstrapInflight && bootstrapInflight.key === key && Date.now() - bootstrapInflight.ts < BOOTSTRAP_DEDUPE_MS) {
    return bootstrapInflight.promise;
  }
  const promise = (async () => {
    const res: AxiosResponse = await api.get("/app-api/platform-order-drafts/bootstrap", {
      headers: authHeaders({ jwtToken, xContext }),
      // supplier_id：入口 supplier 預覽（無 CIS 新客戶）。context 推不出該 supplier 時後端會附加，
      // 客戶才看得到商品；已有 CIS 時後端清單本來就含它，此參數無副作用。
      params: supplierId ? { supplier_id: supplierId } : undefined,
    });
    const data = res?.data?.data;
    const suppliers: SupplierData[] = data?.available_suppliers || [];
    const supplier = supplierId ? suppliers.find((s) => s.supplier_id === supplierId) || null : suppliers[0] || null;
    return { supplier, addresses: data?.available_addresses || [] };
  })();
  bootstrapInflight = { key, ts: Date.now(), promise };
  return promise;
};

/** preview：算費（小計 / 樓層費 / 優惠折抵 / 應付）。平台服務費由前端決定不顯示。 */
export interface KioskPreview {
  subtotal: number;
  fee: number;
  floor_fee: number;
  platform_service_fee: number;
  coupon_discount: number;
  total: number;
  delivery_slot_available: boolean;
  requires_auth: boolean;
}

export const fetchKioskPreview = async ({
  draftId,
  couponId,
  jwtToken,
  xContext,
}: AuthCtx & { draftId: string; couponId: string | null }): Promise<KioskPreview | null> => {
  try {
    const res: AxiosResponse = await api.post(
      `/app-api/platform-order-drafts/${draftId}/preview`,
      { customer_coupon_id: couponId || null },
      { headers: authHeaders({ jwtToken, xContext }) },
    );
    return res?.data?.data ?? null;
  } catch (error) {
    console.error("fetchKioskPreview error:", error);
    return null;
  }
};

/** 建草稿並帶入單一 gas 品項。回傳 draft 物件（給 syncDraft）。 */
export const createDraftWithGas = async ({
  supplierId,
  customerAddressId,
  gasItem,
  jwtToken,
  xContext,
}: AuthCtx & { supplierId: string; customerAddressId: string | number; gasItem: Record<string, any> }) => {
  const res: AxiosResponse = await api.post(
    "/app-api/platform-order-drafts",
    { supplier_id: supplierId, customer_address_id: customerAddressId, gas_list: [gasItem] },
    { headers: authHeaders({ jwtToken, xContext }) },
  );
  const inner = res?.data?.data;
  return inner?.draft || (inner?.draft_id ? inner : null);
};

/**
 * 一鍵重訂：把 last_order.gas_list 轉成 GasItemReqDto 並建草稿。
 * 帶 customer_delivery_id 走 BE upsert；跨地址 race（BE 400）時 retry 一次不帶 id。
 * 對齊 GasShopProducts.autoPopulateFromLastOrder 的行為。
 */
export const reorderFromLast = async ({
  lastOrder,
  supplierId,
  customerAddressId,
  jwtToken,
  xContext,
}: AuthCtx & { lastOrder: LastOrder; supplierId: string; customerAddressId: string | number }) => {
  const mapItem = (item: LastOrderGasItem, withId: boolean) => {
    const loc = item.install_location!;
    return {
      type: item.type,
      price_id: item.price_id,
      gas_id: item.gas_id,
      gas_type: item.gas_type,
      kilogram: item.kilogram,
      unit_price: item.unit_price,
      qty: item.qty,
      usage_location: loc.usage_location,
      note: loc.note,
      floor: loc.floor,
      is_elevator: loc.is_elevator,
      ...(withId && loc.customer_delivery_id != null ? { customer_delivery_id: loc.customer_delivery_id } : {}),
    };
  };
  const eligible = (lastOrder?.gas_list || []).filter((it) => it.install_location !== null);
  if (eligible.length === 0) return null;

  const postOnce = (withId: boolean) =>
    api.post(
      "/app-api/platform-order-drafts",
      { supplier_id: supplierId, customer_address_id: customerAddressId, gas_list: eligible.map((it) => mapItem(it, withId)) },
      { headers: authHeaders({ jwtToken, xContext }) },
    );

  try {
    const res = await postOnce(true);
    return res?.data?.data?.draft ?? null;
  } catch (err) {
    const msg = (err as AxiosError<{ message?: string }>)?.response?.data?.message ?? "";
    const isAddressMismatch =
      String(msg).includes("does not belong to draft customer_address_id") || String(msg).includes("moved to another address");
    if (!isAddressMismatch) {
      console.warn("reorderFromLast failed", err);
      return null;
    }
    try {
      const res = await postOnce(false);
      return res?.data?.data?.draft ?? null;
    } catch (retryErr) {
      console.warn("reorderFromLast retry-without-id failed", retryErr);
      return null;
    }
  }
};
